/**
 * What somebody last opened — the vocabulary the lookups and the API share.
 *
 * A lookup that opens on "the first eight alphabetically" is a lookup that
 * shows the same eight to everybody, for ever. What a person wants before
 * they have typed anything is the property they were on ten minutes ago, so
 * the server keeps a per-person trail (`recent_records`, migration 0073) and
 * every lookup reads it first.
 *
 * The kinds are `RECORD_TYPES` plus `user`: a salesperson is not a record with
 * a page of its own, but picking one is exactly the same problem, and the
 * "Salesperson" combo on a new job deserves the same answer as the "Property"
 * one beside it.
 */
import { z } from 'zod';
import { RECORD_TYPES } from './records.js';
/*
 * Not `work_item` (the Visit page's record, decision 17): a visit is opened
 * from its job, the Visits list or the schedule, never picked in a lookup.
 * Not `quote` either (folder 52): a quote is a record only for its Activity
 * tab, and is opened from its job — no lookup offers it.
 */
export const LOOKUP_KINDS = [
    ...RECORD_TYPES.filter((type) => type !== 'work_item' && type !== 'quote'),
    'user',
];
export const lookupKindSchema = z.enum(LOOKUP_KINDS);
/**
 * How many rows a lookup asks for before anything is typed.
 *
 * Eight was the old number and it was too few to be useful and too few to be
 * honest — with four hundred properties on file, eight of them is not a list,
 * it is a sample nobody can tell is a sample. Twenty-five fills the menu,
 * scrolls once, and the footer says how many there are altogether so the
 * person knows to keep typing rather than to keep scrolling.
 */
export const LOOKUP_PAGE = 25;
/** One row of a lookup, as the recents endpoint answers it. */
export const recentRecordSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    sub: z.string().nullable(),
    /**
     * The one or two named facts a caller needs *back* from a row, rather than
     * printed on it — the new-job form shows "Customer: Keystone Builders"
     * under the property it just picked, and that is the account's name, not a
     * substring of the row's second line.
     */
    meta: z.record(z.string(), z.string()).nullable(),
    seenAt: z.string(),
});
//# sourceMappingURL=recents.js.map